import type { NavigationExtras } from '@angular/router';
import type { FlowDefinitionV2 } from '../../workflows/workflow.types';
import {
  CREATE_AGENT_AGENT_QUERY_PARAM,
  CREATE_AGENT_BASE,
  CREATE_AGENT_STEPS,
  type CreateAgentStepSegment,
} from './create-agent.paths';

export type { CreateAgentStepSegment } from './create-agent.paths';

const STEP_LABELS: Record<CreateAgentStepSegment, string> = {
  'agent-details': 'Agent Details',
  'training-questions': 'Training questions',
  'simulate-agent': 'Simulate Agent',
  evaluate: 'Evaluate',
  'deploy-retain': 'Deploy/Retain',
};

export function flowIncludesTrainingQuestionsStep(
  definition: FlowDefinitionV2 | null | undefined,
): boolean {
  return (definition?.nodes ?? []).some((n) => n.nodeCode === 'QUESTIONS');
}

/**
 * Top-level create-agent steps. Training questions live inside Simulate Agent at the workflow's
 * `questions` screen position — not as a fixed step after Agent Details.
 */
export function wizardSegments(): readonly CreateAgentStepSegment[] {
  return CREATE_AGENT_STEPS.filter((s) => s !== 'training-questions');
}

export function wizardStepLabels(): readonly string[] {
  return wizardSegments().map((s) => STEP_LABELS[s]);
}

export function navigateCreateAgentSegment(
  segment: CreateAgentStepSegment,
  agentKey?: string | null,
): { commands: string[]; extras: NavigationExtras } {
  const k = (agentKey ?? '').trim();
  const extras: NavigationExtras = k
    ? { queryParams: { [CREATE_AGENT_AGENT_QUERY_PARAM]: k, agentId: null } }
    : { queryParams: { [CREATE_AGENT_AGENT_QUERY_PARAM]: null, agentId: null } };
  return { commands: [CREATE_AGENT_BASE, segment], extras };
}

export function segmentAfterAgentDetails(): CreateAgentStepSegment {
  return 'simulate-agent';
}

export function previousWizardSegment(current: CreateAgentStepSegment): CreateAgentStepSegment | null {
  const segs = wizardSegments();
  const i = segs.indexOf(current);
  return i > 0 ? segs[i - 1] : null;
}

export function nextWizardSegment(current: CreateAgentStepSegment): CreateAgentStepSegment | null {
  const segs = wizardSegments();
  const i = segs.indexOf(current);
  return i >= 0 && i < segs.length - 1 ? segs[i + 1] : null;
}

/** 1-based index for the stepper UI from the current URL. */
export function displayStepFromUrl(url: string): number {
  const path = url.split('?')[0];
  const parts = path.split('/').filter(Boolean);
  let segment: CreateAgentStepSegment = 'agent-details';
  if (parts.includes('simulate-agent') || parts.includes('training-questions')) {
    segment = 'simulate-agent';
  } else {
    const last = parts.at(-1) ?? '';
    if (CREATE_AGENT_STEPS.includes(last as CreateAgentStepSegment)) {
      segment = last as CreateAgentStepSegment;
    }
  }
  const segs = wizardSegments();
  const idx = segs.indexOf(segment);
  return idx >= 0 ? idx + 1 : 1;
}

export function stepIconSegment(segment: CreateAgentStepSegment): string {
  switch (segment) {
    case 'agent-details':
      return 'user';
    case 'training-questions':
      return 'list-check';
    case 'simulate-agent':
      return 'play-circle';
    case 'evaluate':
      return 'circle-check';
    case 'deploy-retain':
      return 'server';
    default:
      return 'user';
  }
}
